import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './AboutMe.css';

const AboutMe = () => {
  const titleRef = useRef(null);
  const descriptionRef1 = useRef(null);
  const descriptionRef2 = useRef(null);
  const descriptionRef3 = useRef(null);
  const descriptionRef4 = useRef(null);
  const descriptionRef5 = useRef(null);
  const aboutMeRef = useRef(null);

  useEffect(() => {
    // Set initial state - elements hidden and positioned
    gsap.set(titleRef.current, { 
      opacity: 0, 
      x: -100 
    });
    
    gsap.set([descriptionRef1.current, descriptionRef3.current, descriptionRef5.current], { 
      opacity: 0, 
      x: -100 
    });
    
    gsap.set([descriptionRef2.current, descriptionRef4.current], { 
      opacity: 0, 
      x: 100 
    });

    // Intersection Observer for trigger animations when section appears
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Title animation - slides in from left
          gsap.to(titleRef.current, {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power3.out',
            delay: 0.2
          });

          // Paragraph 1, 3 & 5 - slide in from left with staggered timing
          gsap.to([descriptionRef1.current, descriptionRef3.current, descriptionRef5.current], {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power3.out',
            stagger: 0.3,
            delay: 0.4
          });

          // Paragraph 2 & 4 - slide in from right with staggered timing
          gsap.to([descriptionRef2.current, descriptionRef4.current], {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power3.out',
            stagger: 0.3,
            delay: 0.6
          });
        }
      });
    }, {
      threshold: 0.3,
      rootMargin: '0px 0px -100px 0px'
    });

    const currentRef = aboutMeRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);
  return (
    <section className="about-me" ref={aboutMeRef}>
      <div className="about-me-content">
        <h2 className="about-me-title" ref={titleRef}>About Me</h2>
        <div className='about-me-description'>
          <p className="about-me-text" ref={descriptionRef1}>
            Hi, I&apos;m Sunil Anand - a hands-on technical leader and full-stack architect with 22+ years of
            experience building scalable, accessible, and high-performance web applications.
          </p>
          <p className="about-me-text" ref={descriptionRef2}>
          I specialize in React, TypeScript, Node.js, and modern engineering practices, with deep expertise in architecting WCAG 2.2 AA-compliant eLearning platforms used by global audiences. My work combines user experience, accessibility, and performance with disciplined engineering practices such as Agile, TDD, CI/CD, and Extreme Programming.
          </p>
          <p className="about-me-text" ref={descriptionRef3}>
            I am now focused on designing <strong>AI-powered applications</strong> by integrating agentic workflows,
            LLM orchestration, and intelligent automation into web systems. I work with technologies like LangChain,
            LLaMA, and PyTorch to build solutions that go beyond static interfaces - enabling adaptive behavior,
            decision-making flows, and context-aware user experiences.
          </p>
          <p className="about-me-text" ref={descriptionRef4}>
            My current focus includes:
          </p>
          <ul className="about-me-list">
            <li>Agentic architectures for autonomous task execution</li>
            <li>LLM orchestration and prompt pipelines</li>
            <li>AI-assisted content generation and evaluation systems</li>
            <li>Intelligent learning platforms with adaptive user journeys</li>
          </ul>
          <p className="about-me-text" ref={descriptionRef5}>
            Alongside hands-on development, I lead distributed teams, mentor engineers, and drive technical excellence
            through structured collaboration, code reviews, and knowledge sharing.
          </p>
          <p className="about-me-text">
            I continuously experiment with emerging technologies to bridge the gap between traditional web platforms and
            intelligent systems - building solutions that are not just scalable, but also adaptive and future-ready.
          </p>
          <p className="about-me-text about-me-mission">
            <strong>Mission:</strong> To architect accessible, intelligent, and AI-driven digital experiences while
            enabling teams and organizations to evolve with the next generation of technology.
          </p>
        </div>
      </div>
    </section>
  );
}

export default AboutMe;
